import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui';
import {
  IdentifyEventUser, GetFunctionsByEventId, GetGuestInvitesByMobile, GetGuestsByEventIdAndCreatedBy,
  CheckGuestDuplicate, AddGuestToFunctions, UpdateGuest, SetGuestFunctions, SubmitGuestRsvp,
} from '../../api/api';
import {
  isMarriageEventType, isPastFunction, nameInput, tenDigits, toggleWithWedding, newGuestFunctionIds, isAutoFunction,
} from '../../utils/helpers';
import { getGuestSession, clearGuestSession } from '../../utils/guestSession';

// The guest screen (/guest) — all logic, shared by GuestHome.desktop and
// GuestHome.mobile (the APK uses the mobile one). A signed-in GUEST of one
// event (utils/guestSession) can:
//   • see their own invitations (the functions they are invited to) and RSVP;
//   • add the people they want to bring, to the functions they are invited to
//     themselves (all open functions if their own number is not on the list);
//   • edit / remove the people THEY added - nobody else's.
// Every person they add is saved with CreatedBy = the guest's UserId, their
// side, Visibility 'Common' (so members see them too) and AddedByName/Mobile,
// which is how admins and members see "added by <guest>" (+ the AddedByGuest
// flag the API derives). Someone already invited to one of those functions -
// same number, or same surname + name - is refused with who added them.

const blankForm = (FunctionIds = []) => ({ Surname: '', GuestName: '', MobileNumber: '', FunctionIds, weddingOff: false });
const idsOf = (row) => String(row?.FunctionIds || row?.FunctionId || '').split(',').map(v => v.trim()).filter(Boolean);

export function useGuestHome() {
  const nav = useNavigate();
  const toast = useToast();
  const [session] = useState(() => getGuestSession());
  const user = session?.user;
  const event = session?.event;
  const me = user?.UserId;
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);

  const [functions, setFunctions] = useState([]);
  const [invites, setInvites] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRsvp, setSavingRsvp] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [addedCount, setAddedCount] = useState(0);

  const signOut = useCallback((msg) => {
    clearGuestSession();
    nav('/', { replace: true, state: msg ? { msg } : undefined });
  }, [nav]);

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const [fns, inv, own] = await Promise.all([
        GetFunctionsByEventId({ EventId: event.EventId }),
        GetGuestInvitesByMobile({ MobileNumber: session.mobile }),
        GetGuestsByEventIdAndCreatedBy({ EventId: event.EventId, CreatedBy: me }),
      ]);
      setFunctions(Array.isArray(fns) ? fns : []);
      setInvites((Array.isArray(inv) ? inv : []).filter(r => String(r.EventId) === String(event.EventId)));
      setMine(Array.isArray(own) ? own : []);
    } catch {
      toast('Could not load - check your connection');
    } finally {
      setLoading(false);
    }
  }, [session, event, me, toast]);

  // No session → sign-in. Otherwise make sure this number is still a guest
  // (an admin may have removed it, or made them a member).
  useEffect(() => {
    if (!session) { nav('/', { replace: true }); return; }
    load();
    IdentifyEventUser({ MobileNumber: session.mobile, EventId: event.EventId })
      .then(r => {
        if (r?.kind === 'staff') signOut('You are now part of the team - sign in with your password.');
        else if (r?.kind === 'not_found' || r?.kind === 'inactive') signOut('This number can no longer sign in. Please contact the family.');
      })
      .catch(() => { /* offline: keep the screen */ });
  }, [session, event, nav, load, signOut]);

  // ── My invitations: one card per invitation (GuestId), its functions listed ──
  const invitations = useMemo(() => {
    const byGuest = new Map();
    invites.forEach(r => {
      if (!byGuest.has(r.GuestId)) byGuest.set(r.GuestId, { ...r, functions: [] });
      const g = byGuest.get(r.GuestId);
      if (r.FunctionId && !g.functions.some(f => String(f.FunctionId) === String(r.FunctionId))) {
        g.functions.push({
          FunctionId: r.FunctionId, FunctionName: r.FunctionName, FunctionDate: r.FunctionDate,
          FunctionTime: r.FunctionTime, Venue: r.Venue, VenueAddress: r.VenueAddress, City: r.City, GpsLocation: r.GpsLocation,
        });
      }
    });
    return [...byGuest.values()];
  }, [invites]);

  const rsvp = async (inv, answer) => {
    setSavingRsvp(inv.GuestId);
    try {
      const ConfirmedCount = answer === 'No' ? 0 : (inv.ConfirmedCount || inv.ExpectedCount || 1);
      await SubmitGuestRsvp({ GuestId: inv.GuestId, MobileNumber: session.mobile, RSVPStatus: answer, ConfirmedCount });
      setInvites(list => list.map(r => (r.GuestId === inv.GuestId ? { ...r, RSVPStatus: answer, ConfirmedCount } : r)));
      toast(answer === 'Yes' ? 'See you there!' : 'Response saved');
    } catch {
      toast('Could not save your response');
    } finally {
      setSavingRsvp(null);
    }
  };

  // ── The functions a guest may invite people to ───────────────────────────
  // Their own open functions; everything open when their number is not on the
  // list (a known user who was not invited directly).
  const invitedIds = useMemo(() => new Set(invites.map(r => String(r.FunctionId))), [invites]);
  const pickerFunctions = useMemo(() => {
    const open = functions.filter(f => !isPastFunction(f));
    const own = open.filter(f => invitedIds.has(String(f.FunctionId)));
    return own.length ? own : open;
  }, [functions, invitedIds]);
  // New people start with the Reception (everyone) and the Wedding ticked.
  const defaultIds = useMemo(() => newGuestFunctionIds(
    pickerFunctions.filter(isAutoFunction).map(f => String(f.FunctionId)), pickerFunctions,
  ), [pickerFunctions]);

  // ── Add / edit the people I bring ────────────────────────────────────────
  const openAdd = () => {
    if (!pickerFunctions.length) { toast('There is no open function to invite to'); return; }
    setEditing(null); setForm(blankForm(defaultIds)); setFormErr(''); setAddedCount(0); setFormOpen(true);
  };
  const openEdit = (row) => {
    const ids = idsOf(row);
    setEditing(row);
    setForm({
      Surname: row.Surname || '', GuestName: row.GuestName || '', MobileNumber: row.MobileNumber || '',
      FunctionIds: ids, weddingOff: ids.length > 0 && !ids.some(id => isAutoFunction(functions.find(f => String(f.FunctionId) === id))),
    });
    setFormErr(''); setFormOpen(true);
  };
  const closeForm = () => { if (!saving) setFormOpen(false); };
  const setField = (k, v) => {
    setFormErr('');
    setForm(f => ({ ...f, [k]: k === 'MobileNumber' ? tenDigits(v) : nameInput(v) }));
  };
  const toggleFunction = (id) => {
    setFormErr('');
    setForm(f => {
      const res = toggleWithWedding(f.FunctionIds, id, pickerFunctions, f.weddingOff);
      return { ...f, FunctionIds: res.ids, weddingOff: res.weddingOff };
    });
  };

  const save = async ({ addNext = false } = {}) => {
    const GuestName = String(form.GuestName).trim();
    const Surname = String(form.Surname).trim();
    const MobileNumber = String(form.MobileNumber).trim();
    if (!GuestName) { setFormErr('Enter the name'); return; }
    if (MobileNumber && !/^\d{10}$/.test(MobileNumber)) { setFormErr('A mobile number must be 10 digits'); return; }
    if (MobileNumber && MobileNumber === session.mobile) { setFormErr('That is your own number'); return; }
    if (!form.FunctionIds.length) { setFormErr('Tick at least one function'); return; }
    setSaving(true); setFormErr('');
    try {
      // Already invited to one of these functions? Say who added them.
      const dup = await CheckGuestDuplicate({
        EventId: event.EventId, FunctionIds: form.FunctionIds, MobileNumber, Surname, GuestName,
        ExceptGuestId: editing?.GuestId || 0,
      });
      if (Array.isArray(dup) && dup.length) {
        const d = dup[0];
        const who = [d.Surname, d.GuestName].filter(Boolean).join(' ') || GuestName;
        setFormErr(`${who} is already added${d.AddedBy ? ` by ${d.AddedBy}` : ''}${d.FunctionNames ? ` (${d.FunctionNames})` : ''}.`);
        return;
      }
      if (editing) {
        await UpdateGuest({
          data: { Surname: Surname || null, GuestName, MobileNumber: MobileNumber || null, UpdatedBy: me },
          where: `GuestId = ${Number(editing.GuestId)} and CreatedBy = ${Number(me)}`,
        });
        await SetGuestFunctions({
          GuestId: editing.GuestId, EventId: event.EventId, OrganiserId: event.OrganiserId,
          FunctionIds: form.FunctionIds, UpdatedBy: me,
        });
        toast('Saved');
        setFormOpen(false);
      } else {
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: event.OrganiserId, FunctionIds: form.FunctionIds,
          Surname: Surname || null, GuestName, MobileNumber: MobileNumber || null,
          Side: isMarriage ? (session.side || 'Common') : 'Common',
          Visibility: 'Common', ExpectedCount: 1, InviteStatus: 'Pending',
          IsFamilyHead: 0, FamilyMembers: [],
          AddedByName: [user.Surname, user.Name].filter(Boolean).join(' ') || user.Name || null,
          AddedByMobile: session.mobile, Valid: 1, CreatedBy: me, UpdatedBy: me,
        });
        toast(`${GuestName} added`);
        if (addNext) {
          setAddedCount(n => n + 1);
          // Same house, same functions: keep the surname and the ticks.
          setForm(f => ({ ...blankForm(f.FunctionIds), Surname: f.Surname, weddingOff: f.weddingOff }));
        } else setFormOpen(false);
      }
      load();
    } catch {
      setFormErr('Could not save - check your connection and try again');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${[row.Surname, row.GuestName].filter(Boolean).join(' ') || 'this guest'} from your list?`)) return;
    try {
      await UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId = ${Number(row.GuestId)} and CreatedBy = ${Number(me)}` });
      toast('Removed');
      load();
    } catch { toast('Could not remove'); }
  };

  const fnName = (id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '';
  const displayName = [user?.Name, user?.Surname].filter(Boolean).join(' ') || session?.mobile || '';

  return {
    ready: !!session, event, displayName, mobile: session?.mobile, loading,
    invitations, rsvp, savingRsvp,
    mine, pickerFunctions, fnName, idsOf,
    formOpen, editing, form, formErr, saving, addedCount,
    openAdd, openEdit, closeForm, setField, toggleFunction, save, remove,
    signOut: () => signOut(),
  };
}
